<template>
    <v-autocomplete
        :item-text="itemText"
        :item-value="itemValue"
        :value="value"
        :search-input.sync="search"
        :items="items"
        v-bind="{
            dense: true,
            outlined: true,
            multiple: true,
            chips: true,
            smallChips: true,
            deletableChips: true,
            hideDetails: 'auto',
            noFilter: true,
            ...$attrs,
        }"
        @keydown.enter.stop.prevent="() => {}"
        v-on="$listeners"
    >
        <template #item="{ parent, item, on, attrs }">
            <slot name="item" :parent="parent" :item="item" :on="on" :attrs="attrs">
                <div>{{ item[itemText] }}</div>
            </slot>
        </template>
    </v-autocomplete>
</template>

<script>
import { delayed } from '@/helpers/tools';
import { BaseCollection } from '@/models/BaseCollection';

/**
 * MultiSelect (v-autocomplete with multiple) with the ability to load item remotely via collection.
 */
export default {
    name: 'MultiSelect',

    props: {
        value: {
            type: Array,
            default: () => [],
        },
        queryParam: {
            type: String,
            default: 'query',
        },
        collectionClass: {
            type: BaseCollection,
            default: null,
            required: false, // either collectionClass or collection is required
        },
        collection: {
            type: BaseCollection,
            default: null,
            required: false, // either collectionClass or collection is required
        },
        itemValue: {
            type: String,
            default: 'id',
        },
        itemText: {
            type: String,
            default: 'title',
        },
    },

    data() {
        let valueCollection = null;
        if (this.collection) {
            valueCollection = this.collection;
        } else {
            valueCollection = new this.collectionClass();
            if (this.value?.length > 0) {
                valueCollection.setFilter(this.itemValue, this.value, 'in');
                valueCollection.query();
            }
        }

        return {
            search: null,
            valueCollection,
            searchCollection: this.collection ? this.collection : new this.collectionClass(),
        };
    },

    computed: {
        loading() {
            return this.valueCollection.loading || this.searchCollection.loading;
        },
        items() {
            return [...this.searchCollection.models, ...(this.value?.length > 0 ? this.valueCollection.models : [])];
        },
    },

    watch: {
        search(newValue) {
            this.executeSearch(newValue);
        },
        loading: {
            handler(newValue) {
                this.$emit('loading', newValue);
            },
            immediate: true,
        },

        value(newValue, oldValue) {
            const added = newValue?.filter((val) => !oldValue?.includes(val)) || [];
            const removed = oldValue?.filter((val) => !newValue?.includes(val)) || [];
            for (const val of added) {
                const toAdd = this.searchCollection.find({ id: val });
                if (toAdd) {
                    this.valueCollection.add(toAdd);
                }
            }
            for (const val of removed) {
                const toRemove = this.searchCollection.find({ id: val });
                if (toRemove) {
                    this.valueCollection.remove(toRemove);
                }
            }
            if (newValue !== oldValue) {
                this.search = null;
            }
        },
    },

    methods: {
        executeSearch: delayed(function (val) {
            if (!this.collection && val?.length >= 3) {
                this.searchCollection.query({ [this.queryParam]: val });
            }
        }),
    },
};
</script>

<style lang="scss" scoped></style>
